<template>
  <div class="login-page" :class="{ 'is-dark': isDark }">
    <div class="login-bg-decoration"></div>
    
    <!-- Theme Toggle -->
    <div class="theme-toggle">
      <el-button 
        type="text" 
        :icon="isDark ? 'el-icon-sunny' : 'el-icon-moon'" 
        @click="toggleTheme"
        class="theme-btn"
      ></el-button>
    </div>

    <div class="login-card">
      <div class="logo-section">
        <img :src="logoUrl" alt="PsyScale Logo" class="logo" />
      </div>
      <div class="login-header">
        <div class="login-title">{{ $store.state.settings.login_title || 'PsyScale 心理测评系统' }}</div>
        <div class="login-subtitle">{{ $store.state.settings.login_subtitle || '专为心理诊所设计的现代化测评方案' }}</div>
      </div>

      <el-form :model="form" :rules="rules" ref="form" @submit.native.prevent size="medium">
        <el-form-item prop="username">
          <el-input 
            v-model="form.username" 
            placeholder="用户名" 
            prefix-icon="el-icon-user"
            autocomplete="username">
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="form.password"
            placeholder="密码"
            prefix-icon="el-icon-lock"
            show-password
            autocomplete="current-password"
            @keyup.enter.native="onSubmit"
          ></el-input>
        </el-form-item>

        <el-button type="primary" class="login-btn" :loading="loading" @click="onSubmit">登 录</el-button>
      </el-form>
    </div>
    
    <div class="footer-copyright">
      &copy; {{ new Date().getFullYear() }} PsyScale Clinical Assessment System
    </div>
  </div>
</template>

<script>
import { login } from '@/api/auth'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'Login',
  data() {
    return {
      isDark: false,
      loading: false,
      form: {
        username: '',
        password: '',
      },
      rules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
      },
    }
  },
  created() {
    // Sync with global theme setting
    const saved = localStorage.getItem('theme')
    if (saved === 'dark') {
      this.isDark = true
    }
  },
  watch: {
    isDark: {
      immediate: true,
      handler(val) {
        if (val) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      }
    }
  },
  computed: {
    logoUrl() {
      // 优先使用后台配置的 Logo，如果没有则回退到默认
      const s = this.$store.state.settings
      if (s && s.system_logo) {
        return s.system_logo
      }
      return require('@/assets/logo.png')
    }
  },
  methods: {
    toggleTheme() {
      this.isDark = !this.isDark
      localStorage.setItem('theme', this.isDark ? 'dark' : 'light')
    },
    async onSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return

        this.loading = true
        try {
          const resp = await login(this.form)
          const tokens = resp.data || {}
          await this.$store.dispatch('login', { access: tokens.access, refresh: tokens.refresh })

          const next = this.$route.query.next || '/app/visitors'
          this.$router.replace(next)
        } catch (e) {
          notifyApiError(e, '登录失败，请检查用户名或密码')
        } finally {
          this.loading = false
        }
      })
    },
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* Light mode (default) */
  background: #f1f5f9;
  background: radial-gradient(circle at 50% 0%, #e0f2fe 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;
  transition: background 0.5s ease;
}

/* Dark mode overrides */
.login-page.is-dark {
  background: #0f172a;
  background: radial-gradient(circle at 50% 0%, #1e293b 0%, #0f172a 100%);
}

.theme-toggle {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
}

.theme-btn {
  font-size: 24px;
  color: #64748b;
  transition: color 0.3s;
}

.theme-btn:hover {
  color: #409EFF;
}

.is-dark .theme-btn {
  color: #94a3b8;
}

.is-dark .theme-btn:hover {
  color: #409EFF;
}

/* 装饰性背景圆 */
.login-bg-decoration {
  position: absolute;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 600px;
  /* Light mode */
  background: radial-gradient(circle, rgba(64, 158, 255, 0.15) 0%, rgba(64, 158, 255, 0) 70%);
  border-radius: 50%;
  pointer-events: none;
  transition: background 0.5s ease;
}

.is-dark .login-bg-decoration {
  background: radial-gradient(circle, rgba(64, 158, 255, 0.08) 0%, rgba(64, 158, 255, 0) 70%);
}

.login-card {
  width: 400px;
  /* Light mode */
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 20px 50px rgba(64, 158, 255, 0.1);
  z-index: 1;
  transition: transform 0.3s ease, background 0.5s ease, border-color 0.5s ease, box-shadow 0.5s ease;
}

.is-dark .login-card {
  background: rgba(30, 41, 59, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.login-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 25px 60px rgba(64, 158, 255, 0.15);
}

.is-dark .login-card:hover {
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
}

.logo-section {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.logo {
  width: 80px;
  height: 80px;
  filter: drop-shadow(0 4px 12px rgba(64, 158, 255, 0.2));
  transition: filter 0.5s ease;
}

.is-dark .logo {
  filter: drop-shadow(0 4px 12px rgba(64, 158, 255, 0.3));
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-title {
  font-size: 24px;
  font-weight: 800;
  color: #1e293b; /* Light mode text */
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  transition: color 0.5s ease;
}

.is-dark .login-title {
  color: #ffffff;
}

.login-subtitle {
  font-size: 14px;
  color: #64748b; /* Light mode text */
  font-weight: 500;
  transition: color 0.5s ease;
}

.is-dark .login-subtitle {
  color: #94a3b8;
}

/* 输入框样式覆盖 */
::v-deep .el-input__inner {
  /* Light mode */
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #e2e8f0;
  color: #1e293b;
  height: 44px;
  border-radius: 8px;
  transition: all 0.3s;
}

.is-dark ::v-deep .el-input__inner {
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
}

::v-deep .el-input__inner:focus {
  border-color: #409EFF;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.is-dark ::v-deep .el-input__inner:focus {
  background: rgba(15, 23, 42, 0.8);
}

::v-deep .el-input__inner::placeholder {
  color: #94a3b8;
}

::v-deep .el-input__prefix {
  left: 8px;
  color: #64748b;
  transition: color 0.5s ease;
}

::v-deep .el-form-item {
  margin-bottom: 24px;
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  margin-top: 8px;
  background: linear-gradient(90deg, #409EFF 0%, #3a8ee6 100%);
  border: none;
  box-shadow: 0 4px 15px rgba(64, 158, 255, 0.3);
  transition: all 0.3s;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
}

.login-btn:active {
  transform: translateY(1px);
}

.footer-copyright {
  position: absolute;
  bottom: 24px;
  color: #94a3b8;
  font-size: 12px;
  text-align: center;
  width: 100%;
  transition: color 0.5s ease;
}

.is-dark .footer-copyright {
  color: #64748b;
}
</style>
